/**
 * Division (company brand) contract — shared across backend (validation) and
 * frontend (launch-screen catalog, nav switcher).
 *
 * A "division" is the company brand a user primarily works under. It is chosen
 * on a post-login launch screen and persisted per-user at
 * `user.preferences.division`. It tailors the app experience (branding, and —
 * over time — default filtering) to the user's part of the business.
 *
 * Note: this is a USER-level concept and is intentionally distinct from the
 * product-level `metadata.brand` ('Pro' | 'DJ') field. They may correlate but
 * are not the same axis — a division picker offers the full brand family.
 */
export const DIVISION_IDS = [
    'org',
    'chauvet-professional',
    'chauvet-dj',
    'kino-flo',
    'chamsys',
    'lyntec',
    'other',
];
/**
 * The launch-screen catalog. Logo files live in
 * `packages/frontend/public/divisions/`. Order here is the display order — the
 * org (executive) umbrella leads, then the individual brand divisions, then
 * `other`.
 *
 * `other` is not a company: it is where products with no brand set live, so an
 * admin has somewhere to put them and can hand them to whoever should be
 * cleaning them up. It is a real division in every other respect — assignable,
 * hideable, and selectable on the launch screen — which is why it carries a
 * plain wordmark rather than a logo.
 */
export const DIVISIONS = [
    { id: 'org', label: 'All Divisions', logo: '/divisions/corporate.webp', isOrg: true },
    { id: 'chauvet-professional', label: 'CHAUVET Professional', logo: '/divisions/chauvet-professional.webp' },
    { id: 'chauvet-dj', label: 'CHAUVET DJ', logo: '/divisions/chauvet-dj.webp' },
    { id: 'kino-flo', label: 'Kino Flo', logo: '/divisions/kino-flo.webp' },
    { id: 'chamsys', label: 'ChamSys', logo: '/divisions/chamsys.webp' },
    { id: 'lyntec', label: 'LynTec', logo: '/divisions/lyntec.webp' },
    { id: 'other', label: 'Other', logo: '/divisions/other.svg' },
];
/** The organization-wide (executive) division — the all-access umbrella. */
export const ORG_DIVISION = DIVISIONS.find((d) => d.isOrg);
export function isDivisionId(value) {
    return typeof value === 'string' && DIVISION_IDS.includes(value);
}
/** Look up a division by id, or `undefined` when the id is unknown. */
export function getDivision(id) {
    return DIVISIONS.find((d) => d.id === id);
}
// ─── Access resolution ────────────────────────────────────────────────────
//
// The division axis is the *scope* half of access control (which brand's data
// and features a user may reach); roles remain the *action* half. These helpers
// are the single source of truth for scope resolution — nav gate, route guard,
// data filters, and the launch screen all resolve through them rather than
// inspecting `divisions` directly.
/** The org (executive) division id — the all-access umbrella. */
export const ORG_DIVISION_ID = 'org';
/**
 * Normalize a user's assigned divisions. An absent/empty/all-invalid list is
 * treated as `['org']` (all-access) — this is the no-lockout default: a user
 * created before the field existed, or by an older code path, is never
 * accidentally stranded with zero reach. Invalid ids are dropped; the org id is
 * hoisted to the front when present.
 */
export function normalizeAssignedDivisions(divisions) {
    const valid = (divisions ?? []).filter(isDivisionId);
    const unique = Array.from(new Set(valid));
    if (unique.length === 0)
        return [ORG_DIVISION_ID];
    // Keep catalog order for stable UI, org first.
    return DIVISION_IDS.filter((id) => unique.includes(id));
}
/**
 * Resolve a user's effective division scope from their assigned set and an
 * optional active-view selection.
 *
 * - `assigned` is normalized (see {@link normalizeAssignedDivisions}).
 * - `isOrg` is true when the org umbrella is in the assigned set.
 * - `active` is `activeSelection` when the user may reach it, else the first
 *   assigned division (org members default to `org`).
 */
export function resolveDivisions(user, activeSelection) {
    const assigned = normalizeAssignedDivisions(user.divisions);
    const isOrg = assigned.includes(ORG_DIVISION_ID);
    const canReachActive = !!activeSelection && (isOrg || assigned.includes(activeSelection));
    const active = canReachActive ? activeSelection : assigned[0];
    return { assigned, isOrg, active };
}
/**
 * Whether a user (by assigned divisions) may reach a target division. Org
 * members reach everything. Used by every scope check.
 */
export function canReachDivision(divisions, target) {
    const assigned = normalizeAssignedDivisions(divisions);
    return assigned.includes(ORG_DIVISION_ID) || assigned.includes(target);
}
/**
 * Brand → division mapping (authoritative, from the sheet Brand column). Includes
 * the case/spelling variants present in the data (`LynTec`/`Lyntec`,
 * `Cinema`/`Kino Flo`). `Trusst` is mapped for completeness though no rows carry
 * it today. `CDJ` was folded into `DJ` on 2026-08-13 — in Smartsheet (the master)
 * and in every product version — so it is no longer part of the vocabulary.
 */
export const PRODUCT_BRAND_DIVISION = {
    Pro: 'chauvet-professional',
    Iluminarc: 'chauvet-professional',
    DJ: 'chauvet-dj',
    Trusst: 'chauvet-dj',
    ChamSys: 'chamsys',
    GeNetix: 'chamsys',
    Cinema: 'kino-flo',
    'Kino Flo': 'kino-flo',
    LynTec: 'lyntec',
    Lyntec: 'lyntec',
    JG: 'lyntec',
};
/**
 * Division a product with a null/blank brand belongs to.
 *
 * `other`, not Professional: an unbranded product is unfiled rather than a Pro
 * one, and parking the whole pile under Professional made 100 products in prod
 * look like theirs. Divisions that want to see them tick Other in
 * "can also see", which is also how they get looked at and given a real brand.
 */
export const NULL_BRAND_DIVISION = 'other';
/**
 * Division a product belongs to, derived from `metadata.brand`.
 *
 * Collapses the 12 brand values (including the `LynTec`/`Lyntec` case variants)
 * onto the 5 real divisions, so callers that need one destination per division —
 * Asana portfolio routing, for one — don't each re-implement the mapping.
 *
 * Returns `undefined` for a brand outside the vocabulary. Callers must decide
 * what an unknown brand means for them; do NOT fall back to a default division,
 * or an unrecognised brand silently lands in whichever bucket happens to be first.
 * A null/blank brand is not unknown — it resolves to `NULL_BRAND_DIVISION`,
 * matching the scoping rules above.
 */
export function divisionForProductBrand(brand) {
    if (brand === null || brand === undefined)
        return NULL_BRAND_DIVISION;
    if (typeof brand !== 'string')
        return undefined;
    const trimmed = brand.trim();
    if (trimmed === '')
        return NULL_BRAND_DIVISION;
    return PRODUCT_BRAND_DIVISION[trimmed];
}
/**
 * Key standing for a product with no brand (null, missing or blank). A real
 * brand is trimmed and non-empty, so the empty string cannot collide with one.
 * It is an ordinary entry in the map — the old hardcoded "null brand defaults to
 * PRO" rule is now the seed value below, and an admin can move it.
 */
export const NO_BRAND_KEY = '';
/** The seed mapping — the previous hardcoded behaviour, expressed per division. */
export const DEFAULT_DIVISION_BRANDS = (() => {
    const out = {};
    for (const [brand, division] of Object.entries(PRODUCT_BRAND_DIVISION)) {
        (out[division] ??= []).push(brand);
    }
    (out[NULL_BRAND_DIVISION] ??= []).push(NO_BRAND_KEY);
    return out;
})();
/**
 * Coerce a stored map into shape: known non-org divisions only, string brands,
 * trimmed and deduplicated. A key whose value is not an array is dropped rather
 * than emptied, so a corrupt entry falls back to the seed instead of hiding
 * every product of that division.
 */
export function normalizeDivisionBrands(input) {
    const src = input && typeof input === 'object' ? input : {};
    const out = {};
    for (const id of DIVISION_IDS) {
        if (id === ORG_DIVISION_ID)
            continue;
        const raw = src[id];
        if (!Array.isArray(raw))
            continue;
        out[id] = Array.from(new Set(raw.filter((b) => typeof b === 'string').map((b) => b.trim())));
    }
    return out;
}
/** The stored map merged over the seed: stored keys win, absent keys keep the seed. */
export function effectiveDivisionBrands(stored) {
    return { ...DEFAULT_DIVISION_BRANDS, ...normalizeDivisionBrands(stored ?? {}) };
}
/** Nothing is shared until an admin ticks a box. */
export const DEFAULT_DIVISION_ALSO_SEES = {};
/**
 * Keep only known divisions on both sides. `org` is dropped as a key (it already
 * reaches everything) and a division naming itself is dropped as a no-op.
 */
export function normalizeDivisionAlsoSees(input) {
    const src = input && typeof input === 'object' ? input : {};
    const out = {};
    for (const id of DIVISION_IDS) {
        if (id === ORG_DIVISION_ID)
            continue;
        const raw = src[id];
        if (!Array.isArray(raw))
            continue;
        const targets = DIVISION_IDS.filter((target) => target !== id && target !== ORG_DIVISION_ID && raw.includes(target));
        if (targets.length > 0)
            out[id] = targets;
    }
    return out;
}
export const DEFAULT_BRAND_SCOPE = {
    brands: DEFAULT_DIVISION_BRANDS,
    alsoSees: DEFAULT_DIVISION_ALSO_SEES,
};
/**
 * Every division whose products these divisions reach: the assigned ones plus
 * one hop of "can also see".
 */
export function reachableDivisions(assigned, alsoSees = DEFAULT_DIVISION_ALSO_SEES) {
    const out = new Set(assigned);
    for (const id of assigned)
        for (const target of alsoSees[id] ?? [])
            out.add(target);
    return [...out];
}
/**
 * True when the caller reaches every brand — an org member, or an unscoped
 * caller (API key, internal job) whose assignment resolves to org.
 *
 * Worth checking before `allowedProductBrands`: it is the common case in
 * production and it needs no brand map, so a caller can skip loading one.
 */
export function hasAllBrandAccess(userDivisions) {
    return normalizeAssignedDivisions(userDivisions).includes(ORG_DIVISION_ID);
}
/**
 * The brands a user may see: `'all'` for org members (and for an absent
 * assignment, which resolves to org — API-key and internal callers), otherwise
 * the union over their divisions and everything those divisions can also see.
 *
 * FAIL-CLOSED, by decision: this is an allow-list, so a brand listed under no
 * division is reachable by org only. A new brand value arriving from an import
 * is therefore invisible to brand-scoped users until an admin assigns it — the
 * admin screen lists the brands products actually carry so an unassigned one is
 * visible there rather than being noticed as missing products.
 */
export function allowedProductBrands(userDivisions, scope = DEFAULT_BRAND_SCOPE) {
    const assigned = normalizeAssignedDivisions(userDivisions);
    if (assigned.includes(ORG_DIVISION_ID))
        return 'all';
    const out = new Set();
    for (const id of reachableDivisions(assigned, scope.alsoSees)) {
        for (const brand of scope.brands[id] ?? [])
            out.add(brand);
    }
    return [...out];
}
/**
 * Whether a user may see one product, by its brand. Null/blank/missing brand is
 * the {@link NO_BRAND_KEY} entry. Guards the single-product reads the list-query
 * filter below doesn't cover.
 */
export function canReachProductBrand(userDivisions, brand, scope = DEFAULT_BRAND_SCOPE) {
    const allowed = allowedProductBrands(userDivisions, scope);
    if (allowed === 'all')
        return true;
    const key = typeof brand === 'string' ? brand.trim() : NO_BRAND_KEY;
    return allowed.includes(key);
}
/**
 * Mongo condition scoping a product query to the brands a user may reach.
 * Returns `null` for all-access (org, or an unscoped caller). Merge the returned
 * object into the query's `$match`/filter.
 *
 * An empty `$in` is deliberate, not a bug: a division an admin has given no
 * brands matches no products. That is the fail-closed half of the allow-list.
 */
export function productBrandScopeCondition(userDivisions, scope = DEFAULT_BRAND_SCOPE) {
    const allowed = allowedProductBrands(userDivisions, scope);
    if (allowed === 'all')
        return null;
    const values = allowed.filter((b) => b !== NO_BRAND_KEY);
    // `$in: [null]` also matches a MISSING field, which is how most no-brand
    // products are stored; '' covers the ones written as blank.
    if (allowed.includes(NO_BRAND_KEY))
        values.push(null, '');
    return { 'metadata.brand': { $in: values } };
}
//# sourceMappingURL=division.types.js.map